import { forceUpdate, h } from "@stencil/core";
import { fixAndUpdateLayoutModel, getMousePosition, sizesToGridTemplate, updateComponentsAndDragBar } from "./utils";
import { isRTL } from "../../common/utils";
import { removeItem } from "./remove-item";
const RESIZING_CLASS = "gx-layout-splitter--resizing";
const GRID_TEMPLATE_DIRECTION_CUSTOM_VAR = "--ch-layout-splitter__distribution";
const DIRECTION_CLASS = (direction) => `group direction--${direction}`;
const TEMPLATE_STYLE = (items, itemsInfo) => ({
    [GRID_TEMPLATE_DIRECTION_CUSTOM_VAR]: sizesToGridTemplate(items, itemsInfo, items.length - 1)
});
const getAriaControls = (layoutItems, index) => `${layoutItems[index].id} ${layoutItems[index + 1].id}`;
// Key codes
const ARROW_UP = "ArrowUp";
const ARROW_RIGHT = "ArrowRight";
const ARROW_DOWN = "ArrowDown";
const ARROW_LEFT = "ArrowLeft";
/**
 * @part bar - The bar that divides two columns or two rows
 */
export class ChLayoutSplitter {
    constructor() {
        this.barAccessibleName = "Resize";
        this.dragBarDisabled = false;
        this.incrementWithKeyboard = 2;
        this.layout = {
            id: "root",
            direction: "columns",
            items: []
        };
    }
    #needForRAF = true; // To prevent redundant RAF (request animation frame) calls
    #mouseDownInfo;
    #lastMousePosition;
    #newMousePosition;
    #fixedSizesSumRoot;
    // Distribution of elements
    #itemsInfo = new Map();
    el;
    handleComponentsChange(newLayout) {
        this.updateLayoutInfo(newLayout);
    }
    /**
     * Schedules a new render of the control even if no state changed.
     */
    async refreshLayout() {
        forceUpdate(this);
    }
    /**
     * Removes the item that is identified by the given ID.
     * The layout is rearranged depending on the state of the removed item.
     */
    async removeItem(viewId) {
        const result = removeItem(viewId, this.#itemsInfo);
        if (result.success) {
            // Queue re-renders
            forceUpdate(this);
        }
        return result;
    }
    #handleBarDrag = (event) => {
        event.preventDefault();
        this.#newMousePosition = getMousePosition(event, this.#mouseDownInfo.direction);
        this.#handleBarDragRAF();
    };
    #handleBarDragRAF = (incrementInPx) => {
        if (!this.#needForRAF) {
            return;
        }
        this.#needForRAF = false; // No need to call RAF up until next frame
        requestAnimationFrame(() => {
            this.#needForRAF = true; // RAF now consumes the movement instruction so a new one can come
            updateComponentsAndDragBar(this.#mouseDownInfo, this.#itemsInfo, incrementInPx ?? this.#newMousePosition - this.#lastMousePosition, // Increment in px
            GRID_TEMPLATE_DIRECTION_CUSTOM_VAR);
            // Sync new position with last
            this.#lastMousePosition = this.#newMousePosition;
        });
    };
    // Handler to remove mouse down
    #removeMouseMoveHandler = () => {
        document.removeEventListener("mousemove", this.#handleBarDrag, {
            capture: true
        });
    };
    // Remove mousemove and mouseup handlers when mouseup
    #mouseUpHandler = () => {
        this.#removeMouseMoveHandler();
        document.removeEventListener("mouseup", this.#mouseUpHandler, {
            capture: true
        });
        // Add again pointer-events
        this.el.classList.remove(RESIZING_CLASS);
    };
    #initializeDragBarValuesForResizeProcessing = (direction, index, fixedSizesSum, layoutItems, event) => {
        // Initialize the values needed for drag processing
        const dragBarContainer = event.target.parentElement;
        this.#mouseDownInfo = {
            container: dragBarContainer,
            containerSize: direction === "columns"
                ? dragBarContainer.clientWidth
                : dragBarContainer.clientHeight,
            direction: direction,
            fixedSizesSum: fixedSizesSum,
            itemStartId: layoutItems[index].id,
            itemEndId: layoutItems[index + 1].id,
            layoutItems: layoutItems,
            RTL: isRTL()
        };
        // Remove pointer-events during drag
        this.el.classList.add(RESIZING_CLASS);
    };
    #mouseDownHandler = (direction, index, fixedSizesSum, layoutItems) => (event) => {
        // Necessary to prevent selecting the inner image (or other elements) of
        // the bar item when the mouse is down
        event.preventDefault();
        this.#initializeDragBarValuesForResizeProcessing(direction, index, fixedSizesSum, layoutItems, event);
        // Mouse position
        const currentMousePosition = getMousePosition(event, direction);
        this.#lastMousePosition = currentMousePosition;
        this.#newMousePosition = currentMousePosition; // Also updated in mouse move
        // Add listeners
        document.addEventListener("mousemove", this.#handleBarDrag, {
            capture: true
        });
        document.addEventListener("mouseup", this.#mouseUpHandler, {
            capture: true,
            passive: true
        });
    };
    #handleResize = (direction, index, fixedSizesSum, layoutItems) => (event) => {
        if ((direction === "rows" &&
            event.code !== ARROW_UP &&
            event.code !== ARROW_DOWN) ||
            (direction === "columns" &&
                event.code !== ARROW_LEFT &&
                event.code !== ARROW_RIGHT)) {
            return;
        }
        // Prevent scroll
        event.preventDefault();
        this.#initializeDragBarValuesForResizeProcessing(direction, index, fixedSizesSum, layoutItems, event);
        const positiveIncrement = event.code === ARROW_RIGHT || event.code === ARROW_DOWN;
        this.#handleBarDragRAF(positiveIncrement
            ? this.incrementWithKeyboard
            : -this.incrementWithKeyboard);
    };
    #renderItems = (direction, fixedSizesSum, layoutItems) => {
        const lastComponentIndex = layoutItems.length - 1;
        return layoutItems.map((item, index) => [
            item.items ? (h("div", { id: item.id, class: DIRECTION_CLASS(item.direction), style: TEMPLATE_STYLE(item.items, this.#itemsInfo) }, this.#renderItems(item.direction, this.#itemsInfo.get(item.id).fixedSizesSum, item.items))) : (h("div", { id: item.id, class: "leaf" }, h("slot", { key: item.id, name: item.id }))),
            item.dragBar?.hidden !== true && index !== lastComponentIndex && (h("div", {
                // - - - Accessibility - - -
                role: "separator", "aria-controls": getAriaControls(layoutItems, index), "aria-disabled": this.dragBarDisabled ? "true" : null, "aria-label": this.barAccessibleName, "aria-orientation": direction === "columns" ? "vertical" : "horizontal", title: this.barAccessibleName, tabindex: "0",
                // - - - - - - - - - - - - -
                class: "bar", part: item.dragBar?.part ? `bar ${item.dragBar.part}` : "bar", style: item.dragBar?.size ? { "--size": `${item.dragBar.size}px` } : null, onKeyDown: !this.dragBarDisabled
                    ? this.#handleResize(direction, index, fixedSizesSum, layoutItems)
                    : null, onMouseDown: !this.dragBarDisabled
                    ? this.#mouseDownHandler(direction, index, fixedSizesSum, layoutItems)
                    : null
            }))
        ]);
    };
    updateLayoutInfo(layout) {
        // Clear old information
        this.#itemsInfo.clear();
        if (layout?.items?.length > 0) {
            this.#fixedSizesSumRoot = fixAndUpdateLayoutModel(layout, this.#itemsInfo);
            console.log(this.#itemsInfo);
        }
    }
    connectedCallback() {
        this.updateLayoutInfo(this.layout);
    }
    disconnectedCallback() {
        // Defensive programming. Make sure all event listeners are removed correctly
        this.#mouseUpHandler();
    }
    render() {
        const layoutModel = this.layout;
        if (layoutModel?.items == null) {
            return "";
        }
        return (h("div", { class: DIRECTION_CLASS(layoutModel.direction), style: TEMPLATE_STYLE(layoutModel.items, this.#itemsInfo) }, this.#renderItems(layoutModel.direction, this.#fixedSizesSumRoot, layoutModel.items)));
    }
    static get is() { return "ch-layout-splitter"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["layout-splitter.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["layout-splitter.css"]
        };
    }
    static get properties() {
        return {
            "barAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify the label for the drag bar.\nImportant for accessibility."
                },
                "attribute": "bar-accessible-name",
                "reflect": false,
                "defaultValue": "\"Resize\""
            },
            "dragBarDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the resize operation is disabled in all\ndrag bars. If `true`, the drag bars are disabled."
                },
                "attribute": "drag-bar-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "incrementWithKeyboard": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the resizing increment (in pixel) that is applied when using the\nkeyboard to resize a drag bar."
                },
                "attribute": "increment-with-keyboard",
                "reflect": false,
                "defaultValue": "2"
            },
            "layout": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "LayoutSplitterDistribution",
                    "resolved": "{ id: \"root\"; direction: LayoutSplitterDirection; items: LayoutSplitterDistributionItem[]; }",
                    "references": {
                        "LayoutSplitterDistribution": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/layout-splitter/types.ts::LayoutSplitterDistribution"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the list of component that are displayed. Each component will be\nseparated via a drag bar."
                },
                "defaultValue": "{\n    id: \"root\",\n    direction: \"columns\",\n    items: []\n  }"
            }
        };
    }
    static get methods() {
        return {
            "refreshLayout": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Schedules a new render of the control even if no state changed.",
                    "tags": []
                }
            },
            "removeItem": {
                "complexType": {
                    "signature": "(viewId: string) => Promise<LayoutSplitterItemRemoveResult>",
                    "parameters": [{
                            "name": "viewId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "LayoutSplitterItemRemoveResult": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/layout-splitter/types.ts::LayoutSplitterItemRemoveResult"
                        }
                    },
                    "return": "Promise<LayoutSplitterItemRemoveResult>"
                },
                "docs": {
                    "text": "Removes the item that is identified by the given ID.\nThe layout is rearranged depending on the state of the removed item.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "layout",
                "methodName": "handleComponentsChange"
            }];
    }
}
//# sourceMappingURL=layout-splitter.js.map
